import React, { useState, useEffect } from 'react';
import BpMpTemperatures from './BpMpTemperatures';
import MrTemperatures from './MrTemperatures';
import { BpMpTemperature } from '../models/bp-mp-temperature';
import { MrTemperature } from '../models/mr-temperature';
import { BenchSheetTechnologyType, Entities } from '../models/enums';
import { CrudFunctions } from '../utilities/crud-functions';
import { Decimal3Format } from '../templates/pw-formatting';
import GlobalDialog from './Modal/GlobalDialog';
import { ModalMetadata } from '../models/modal-metadata';
import { ModalMessageType } from '../models/enums';

import DataGrid, {
    Column,
    Paging,
    Button as ColumnButton,
    Toolbar,
    Selection,
    Lookup,
    Editing,
    Item as ToolbarItem
} from 'devextreme-react/data-grid';

import { Popup as DialogPopup } from 'devextreme-react/popup';
import { Button } from 'devextreme-react/button';


export interface Props {
    benchSheetId?: string;
    technologyId: BenchSheetTechnologyType;
    batchId?: string;
    isReadOnly: boolean;
    datagridHeight?: string;
    title?: string;
}

export default function BpMpMrTemperaturesData(props: Props) {
    //BP, MP
    const [bpMpTemperatures, setBpMpTemperatures] =
        useState<BpMpTemperature[]>();
    //MR
    const [mrTemperatures, setMrTemperatures] =
        useState<MrTemperature[]>();

    const [bpMpTemperaturesEditorDialogVisible, setBpMpTemperaturesEditorDialogVisible] =
        useState<boolean>();

    const [mrTemperaturesEditorDialogVisible, setMrTemperaturesEditorDialogVisible] =
        useState<boolean>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();

    useEffect(() => {
        getAllData();
    }, [props.benchSheetId]);

    const groupTitle = () => {
        if(props.technologyId == BenchSheetTechnologyType.BoilingPoint)
            return "Boiling Point  °C";
        else if(props.technologyId == BenchSheetTechnologyType.MeltingPoint)
            return "Melting  Point °C";
    }

    return (
        <div>
            <div className="pw-comp-row">
                <div>
                    <DataGrid
                        height={props.datagridHeight ? props.datagridHeight : "16rem"}
                        keyExpr="temperatureId"
                        dataSource={props.technologyId === BenchSheetTechnologyType.MeltingRange ? mrTemperatures ? mrTemperatures : [] : bpMpTemperatures ? bpMpTemperatures : []}
                        rowAlternationEnabled={true}
                        showBorders={true}>

                        <Paging enabled={false} />
                        <Selection mode='single' />

                        <Column dataField='sampleName' caption='Sample Name' dataType="string" width="8rem" />

                        {props.technologyId === BenchSheetTechnologyType.MeltingRange &&
                            <Column caption='Begin Temp °C' alignment="center">
                                <Column dataField='startTempX' caption='X' dataType="number" 
                                    format={Decimal3Format} width="8rem" />
                                <Column dataField='startTempY' caption='Y' dataType="number" 
                                    format={Decimal3Format} width="8rem" />
                                <Column dataField='startTempZ' caption='Z' dataType="number" 
                                    format={Decimal3Format} width="8rem" />
                            </Column>
                        }
                        {props.technologyId === BenchSheetTechnologyType.MeltingRange &&
                            <Column caption='End Temp °C' alignment="center">
                                <Column dataField='endTempX' caption='X' dataType="number" 
                                    format={Decimal3Format} width="8rem" />
                                <Column dataField='endTempY' caption='Y' dataType="number" 
                                    format={Decimal3Format} width="8rem" />
                                <Column dataField='endTempZ' caption='Z' dataType="number" 
                                    format={Decimal3Format} width="8rem" />
                            </Column>
                        }

                        {props.technologyId !== BenchSheetTechnologyType.MeltingRange &&
                            <Column caption={groupTitle()} alignment="center">
                                <Column dataField='tempX' caption='X' dataType="number" 
                                    format={Decimal3Format} width="8rem" />
                                <Column dataField='tempY' caption='Y' dataType="number" 
                                    format={Decimal3Format} width="8rem" />
                                <Column dataField='tempZ' caption='Z' dataType="number" 
                                    format={Decimal3Format} width="8rem" />
                            </Column>
                        }

                        <Column />

                        <Toolbar>
                            <ToolbarItem location='before'>
                                <div className="pw-datagrid-toolbar-template">
                                    <div className="pw-datagrid-toobar-title">{props.title}</div>
                                </div>
                            </ToolbarItem>

                            <ToolbarItem location="after">
                                <Button type="normal" hint="Edit" text="" icon="inserttable"
                                    disabled={props.isReadOnly}
                                    width="1.8rem" onClick={showTemperaturesEditorDialog} />
                            </ToolbarItem>
                       </Toolbar>
                    </DataGrid>
                </div>
            </div>

            {bpMpTemperaturesEditorDialogVisible &&
                <DialogPopup
                    visible={bpMpTemperaturesEditorDialogVisible}
                    onHiding={hideBpMpTemperaturesEditorDialog}
                    dragEnabled={true}
                   fullScreen={true}
                    hideOnOutsideClick={false}
                    title="BP/MP Temperatures">
                    <BpMpTemperatures benchSheetId={props.benchSheetId} onClose={onCloseBpMpTemperaturesEditor} />
                </DialogPopup>
            }

            {mrTemperaturesEditorDialogVisible &&
                <DialogPopup
                    visible={mrTemperaturesEditorDialogVisible}
                    onHiding={hideMrTemperaturesEditorDialog}
                    dragEnabled={true}
                    fullScreen={true}
                    hideOnOutsideClick={false}
                    title="MR Temperatures">
                    <MrTemperatures benchSheetId={props.benchSheetId} onClose={onCloseMrTemperaturesEditor} />
                </DialogPopup>
            }

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} />
            }
        </div>
    );


    function showTemperaturesEditorDialog() {
        if (props.technologyId === BenchSheetTechnologyType.MeltingRange)
            showMrTemperaturesEditorDialog();
        else
            showBpMpTemperaturesEditorDialog();
    }

    function onCloseBpMpTemperaturesEditor() {
        getTemperatures();
        hideBpMpTemperaturesEditorDialog();
    }

    function onCloseMrTemperaturesEditor() {
        getTemperatures();
        hideMrTemperaturesEditorDialog();
    }
       
    async function getTemperatures() {
        if (props.technologyId === BenchSheetTechnologyType.MeltingRange) {
            await CrudFunctions.getData(`benchsheets/mrtemperatures/${props.benchSheetId}`)
                .then((data) => {
                    setMrTemperatures(data);
                })
                .catch(res => {
                    showMessage(res, ModalMessageType.Error, "Error Retrieving MR Temperatures");
                });
        }
        else {
            await CrudFunctions.getData(`benchsheets/bpmptemperatures/${props.benchSheetId}`)
                .then((data) => {
                    setBpMpTemperatures(data);
                })
                .catch(res => {
                    showMessage(res, ModalMessageType.Error, "Error Retrieving BP/MP Temperatures");
                });
        }
    }

    async function getAllData() {
        await getTemperatures();
    }

    function showBpMpTemperaturesEditorDialog() {
        setBpMpTemperaturesEditorDialogVisible(true);
    }

    function hideBpMpTemperaturesEditorDialog() {
        setBpMpTemperaturesEditorDialogVisible(false);
    }

    function showMrTemperaturesEditorDialog() {
        setMrTemperaturesEditorDialogVisible(true);
    }

    function hideMrTemperaturesEditorDialog() {
        setMrTemperaturesEditorDialogVisible(false);
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }
}
